import * as Yup from 'yup'
import { toMaterialTypeValue, toOfflineSealPayload, toScanTypeValue } from './serializers'
import { makeCoreGetRequest, makeCorePostRequest } from 'utils/api'
import { formatErrorMessage } from 'utils/formatting'
import {
  SealScanData,
  Notification,
  OnSealScanQueryErrorParams
} from 'components/shop-reception/mobile/interfaces'
import { AxiosError } from 'axios'

export const getInitialValues = (fromInventory: boolean, materialType: string) => ({
  materialType: materialType ? toMaterialTypeValue(materialType) : { value: '', label: '' },
  scanType: fromInventory ? toScanTypeValue('DELIVERY') : { value: '', label: '' }
})

export const getValidationSchema = () =>
  Yup.object().shape({
    materialType: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string()
      })
      .required('Tip material este obligatoriu.'),
    scanType: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string()
      })
      .required('Tip scanare este obligatoriu.')
  })

export const handleOnlineEvent = async (
  refetch: () => Promise<any>,
  showNotification: (notification: Notification) => void,
  setNotifications: (notifications: Notification[]) => void,
  deleteOfflineData: () => Promise<void>,
  setNoInternetNotification: (noInternetNotification: Notification) => void
) => {
  setNoInternetNotification(null)
  const offlineData: { data: { offlineSearchCodes?: string[] } } = await refetch()
  if (offlineData) {
    const { data } = offlineData
    const offlineSearchCodes = Array.isArray(data?.offlineSearchCodes)
      ? data.offlineSearchCodes
      : []
    if (offlineSearchCodes) {
      let notificationsArray = []
      for (const code of offlineSearchCodes) {
        try {
          const result = await makeCoreGetRequest(`/shop-reception-mobile/returo/seals/${code}`)
          const { scanSuccess, createdBy, id, selected } = result
          if (!scanSuccess) {
            notificationsArray.push({
              message: `Sigiliul ${code} are status IN PREDARE si a fost scanat de catre ${createdBy}`,
              type: 'error',
              code: code
            })
          } else {
            if (id) {
              if (selected) {
                notificationsArray.push({
                  message: `Sigiliul ${code} a fost deja scanat.`,
                  type: 'error',
                  code: code
                })
              } else {
                notificationsArray.push({
                  message: `Sigiliul ${code} a fost deja predat.`,
                  type: 'error',
                  code: code
                })
              }
            } else {
              notificationsArray.push({
                message: `Sigiliul ${code} a fost scanat cu succes.`,
                type: 'success',
                code: code
              })
              if (data[`offlineMaterialType[${code}]`] && data[`offlineScanType[${code}]`]) {
                await makeCorePostRequest(
                  '/shop-reception-mobile/returo/seals',
                  toOfflineSealPayload(
                    code,
                    data[`offlineMaterialType[${code}]`] as string,
                    data[`offlineScanType[${code}]`] as string
                  )
                )
                notificationsArray.push({
                  message: `Sigiliul ${code} a fost salvat cu succes.`,
                  type: 'success',
                  code: code
                })
              }
            }
          }
        } catch (error) {
          showNotification({
            message: `A aparut o eroare la salvarea datelor offline. ${formatErrorMessage(error)}`,
            type: 'error'
          })
        }
      }
      setNotifications(notificationsArray)
      await deleteOfflineData()
    }
  }
}

export const onSealScanQuerySuccess = (
  data: SealScanData,
  showNotification: (notification: Notification) => void,
  resetValues: () => void,
  setIsScanned: (isScanned: boolean) => void,
  formik: any,
  setSearchCode: (code: string) => void
) => {
  const { scanSuccess, createdBy, id, selected, materialType, scanStatus } = data
  if (!scanSuccess) {
    showNotification({
      message: `Acest sigiliu are status IN PREDARE si a fost scanat de catre ${createdBy}`,
      type: 'error'
    })
    resetValues()
    setIsScanned(false)
  } else {
    if (id) {
      setIsScanned(false)
      if (selected) {
        showNotification({
          message: 'Sigiliul a fost deja scanat.',
          type: 'error'
        })
        formik.setFieldValue('materialType', toMaterialTypeValue(materialType))
        formik.setFieldValue('scanType', toScanTypeValue(scanStatus))
        setSearchCode('')
      } else {
        showNotification({
          message: 'Sigiliul a fost deja predat.',
          type: 'error'
        })
        resetValues()
      }
    } else {
      showNotification({
        message: 'Sigiliul a fost scanat cu succes.',
        type: 'success'
      })
      setIsScanned(true)
    }
  }
}

export const onSealScanQueryError = async ({
  response,
  searchCodes,
  searchCode,
  setSearchCodes,
  saveOfflineData,
  setIsScanned,
  showNotification
}: OnSealScanQueryErrorParams) => {
  if (response?.code === AxiosError.ERR_NETWORK) {
    const newCodes = [...new Set([...searchCodes, searchCode])]
    setSearchCodes(newCodes)
    await saveOfflineData([
      { field: `offlineSearchCode[${searchCode}]`, value: searchCode },
      { field: 'offlineSearchCodes', value: newCodes }
    ])
    setIsScanned(true)
  } else {
    showNotification({
      message: `A aparut o eroare la scanarea sigiliului. ${formatErrorMessage(response)}`,
      type: 'error'
    })
  }
}

export const onSaveSealQueryError = async ({
  response,
  searchCodes,
  searchCode,
  setSearchCodes,
  setSearchCode,
  saveOfflineData,
  formik,
  showNotification
}) => {
  if (response?.code === AxiosError.ERR_NETWORK) {
    const newCodes = [...new Set([...searchCodes, searchCode])]
    setSearchCodes(newCodes)
    await saveOfflineData([
      {
        field: `offlineMaterialType[${searchCode}]`,
        value: formik.values.materialType.value
      },
      { field: `offlineScanType[${searchCode}]`, value: formik.values.scanType.value },
      { field: 'offlineSearchCodes', value: newCodes }
    ])
    setSearchCode('')
  } else {
    showNotification({
      message: `A aparut o eroare la salvarea sigiliului. ${formatErrorMessage(response)}`,
      type: 'error'
    })
  }
}
